<template>
  <div
    class="attribute-panel"
    :class="{ 'is-collapsed': isCollapsed, 'is-expanded-view': isExpandedView }"
    @click="panelClickHandler"
  >
    <collapse-btn
      class="attribute-panel__expand-btn"
      v-if="isCollapsed"
      :hide-text="true"
    ></collapse-btn>
    <div
      class="attribute-panel__header-nav"
      v-if="workbenchHeaderData"
    >
      <div
        class="attribute-panel__header-nav__dashboard"
        @click="navigateToDashboard"
      >
        <v-icon
          dark
          color="#C6CCD0"
          >mdi-view-dashboard</v-icon
        >
        <div class="attribute-panel__header-nav__dashboard_label text-button">Dashboard</div>
      </div>
      <div class="attribute-panel__header-nav__status-details">
        <div class="attribute-panel__header-nav__status-details__segment-type">
          {{ getCampaignName(workbenchHeaderData.xandr_data) }}
        </div>
        <div class="attribute-panel__header-nav__status-details__segment-type">
          {{ getDestinations(workbenchHeaderData.destinations) }}
        </div>
        <v-chip
          label
          dark
          small
          class="attribute-panel__header-nav__status-details__status"
          v-if="workbenchHeaderData && workbenchHeaderData.activation_status && !workbenchHeaderData.dropped"
          >{{ workbenchHeaderData.activation_status }}</v-chip
        >
        <v-chip
          label
          dark
          small
          class="attribute-panel__header-nav__status-details__status"
          v-else-if="workbenchHeaderData.dropped"
          @click="clickedDropped"
          >DROPPED</v-chip
        >
      </div>
    </div>

    <header class="attribute-panel__header">
      <div class="attribute-panel__header__segment-details">
        <div
          class="attribute-panel__header__segment-details__left"
          v-if="workbenchHeaderData"
        >
          <v-row justify="start">
            <v-col
              v-if="!editModeActive"
              class="flex-grow-1 attribute-panel__header__segment-details__left__name"
              md="6"
            >
              {{ workbenchHeaderData.display_name }}
            </v-col>
            <v-col v-if="editModeActive">
              <target-name
                :attributePanel="true"
                :isNewTarget="false"
                :initialValue="originalDisplayName"
                :displayOverride="workbenchHeaderData.display_name"
                :targetType="workbenchHeaderData.target"
                @updateName="val => (targetName = val)"
                @updateNameIsValid="onTargetNameIsValid"
              />
            </v-col>
            <v-col
              v-if="workbenchHeaderData.advertiser && workbenchHeaderData.advertiser.Name && !editModeActive"
              class="flex-shrink-1 attribute-panel__header__segment-details__left__name"
              md="0"
            >
              |
            </v-col>
            <v-col
              v-if="workbenchHeaderData.advertiser && workbenchHeaderData.advertiser.Name && !editModeActive"
              class="flex-grow-1 attribute-panel__header__segment-details__left__name"
              md="5"
            >
              {{ workbenchHeaderData.advertiser.Name }}
            </v-col>
            <v-col v-if="editModeActive">
              <advertiser-select
                ref="advertiserSelect"
                placeholder="Select"
                :attributePanel="true"
                :disabled="isByodTraitPresent"
                @update="val => onUpdateAdvertiser(val)"
                v-model="workbenchHeaderData.advertiser"
                :initialValue="workbenchHeaderData.advertiser"
                :showAdvertiserId="false"
              ></advertiser-select>
            </v-col>
          </v-row>
        </div>
        <div
          class="attribute-panel__header__segment-details__right"
          v-if="targetInfoEditable"
        >
          <v-icon
            @click.native="toggleEditMode"
            dark
            >mdi-pencil</v-icon
          >
        </div>
      </div>
      <div class="attribute-panel__header__search-section">
        <search-input
          class="attribute-panel__header__search-section__search"
          :class="{ 'cursor-disabled': viewOnly }"
          ref="searchInput"
          v-model="searchTerm"
          :debounce="500"
          :locked="viewOnly"
        ></search-input>
      </div>
      <bread-crumbs
        v-show="!isSearchActive"
        :crumbs="crumbs"
        @click="crumbClickHandler"
        :locked="viewOnly"
      ></bread-crumbs>
      <back-btn
        class="attribute-panel__back-btn"
        v-show="isSearchActive"
        @click.native="clearSearch"
        >Back To Browse</back-btn
      >
    </header>

    <div class="attribute-panel__body">
      <small-text v-if="tooManySearchResults">
        Too many results to display ({{ searchResultsCount }}). Please refine your search.
      </small-text>
      <search-results
        ref="searchResults"
        v-else-if="isSearchActive"
        :locked="viewOnly || selectedEpsilonDatasetFamily"
        :results="searchResults"
        :selectedValues="selectedValues"
        :count="searchResultsCount"
        :advertiser="workbenchHeaderData.advertiser"
        @item-selected="onSelect"
      ></search-results>
      <list
        v-if="currentList"
        v-show="!isSearchActive"
        :listGroup="currentList"
        id="attributePanelID"
        :locked="viewOnly || selectedEpsilonDatasetFamily"
        :selectedValues="selectedValues"
        :advertiser="workbenchHeaderData.advertiser"
        @expandNode="itemSelectedHandler"
        @onSelect="onSelect"
      ></list>
    </div>
    <dropped-attributes-modal />
  </div>
</template>

<script>
  import AdvertiserSelect from '@/appComponents/targets/components/AdvertiserSelect'
  import AttributeTree from '../models/attributeTree'
  import BackBtn from './BackButton.vue'
  import BreadCrumbs from './BreadCrumbs.vue'
  import CollapseBtn from './CollapseButton.vue'
  import TargetName from '@/appComponents/targets/createTarget/components/TargetName'
  import DroppedAttributesModal from '@/appComponents/targets/components/modals/DroppedAttributesModal'
  import List from './List.vue'
  import PanelSizeBtn from './PanelSizeBtn'
  import SearchInput from '@/components/common/searchInput'
  import SearchResults from './SearchResults.vue'
  import SearchService from '../services/search'
  import SmallText from './SmallText.vue'
  import { ACTIVATION_STATUS, DATASOURCES, TARGET_TYPE, DESTINATIONS } from '@/appLogic/targets/Constants'
  import {
    SET_ENABLE_SAVE_BUTTON,
    SET_ENABLE_DOWN_STREAM,
    SET_ADVERTISER,
    UPDATE_TARGET_NAME,
  } from '@/appLogic/workbench/common/store/actions'
  import { ROUTE_BACK } from '@/appLogic/route/store/actions'
  import { definitionToListOfTraits } from '@/helpers'
  import { SHOW_DROPPED_ATTRIBUTES_MODEL } from '@/appLogic/targets/currentTarget/store/actions'

  export default {
    components: {
      AdvertiserSelect,
      BackBtn,
      BreadCrumbs,
      CollapseBtn,
      DroppedAttributesModal,
      TargetName,
      List,
      PanelSizeBtn,
      SearchInput,
      SearchResults,
      SmallText,
    },
    props: {
      isCollapsed: {
        type: Boolean,
        default: false,
      },
      isExpandedView: {
        type: Boolean,
        default: false,
      },
      setOfAttributes: {
        type: Array,
        default: () => [],
      },
      onSelect: {
        type: Function,
        default: ({ item, event }) => {},
      },
      workbenchHeaderData: {
        type: Object,
      },
      canEditTargetHeaderData: {
        type: Boolean,
      },
      selectedValues: {
        type: Object,
        default: () => {
          return {}
        },
      },
      viewOnly: {
        type: Boolean,
        default: false,
      },
      datasetFilter: {
        type: Array,
        default: () => {
          return []
        },
      },
    },
    data() {
      return {
        searchService: new SearchService(),
        attributesTree: new AttributeTree(),
        editModeActive: false,
        currentList: null,
        searchResults: [],
        searchTerm: '',
        nameIsValid: null,
        originalDisplayName: null,
        originalAdvertiserId: null,
        campaignName: null,
        targetName: '',
        savingEnabledWhenToggled: false,
      }
    },
    computed: {
      hasSelectedAdvertiser() {
        let wb = this.workbenchHeaderData
        if (wb) {
          if (wb.advertiser) {
            if (wb.advertiser.Name) {
              return true
            }
          }
        }
        return false
      },
      crumbs() {
        return (
          this.currentList &&
          this.currentList.getPath().map(node => {
            return {
              name: node.model.name,
              node,
            }
          })
        )
      },
      tooManySearchResults() {
        return this.searchResultsCount > 500
      },
      searchResultsCount() {
        return this.searchResults.reduce((currValue, resultSet) => resultSet.results.length + currValue, 0)
      },
      isSearchActive() {
        return this.searchTerm.length > 0
      },
      isAddressable() {
        const destinations = (this.workbenchHeaderData && this.workbenchHeaderData.destinations) || []
        return destinations.some(d =>
          [DESTINATIONS.DIRECTV, DESTINATIONS.VIZIO, DESTINATIONS.CANOE_LINEAR, DESTINATIONS.CANOE_VOD].includes(d),
        )
      },
      isLive() {
        return (
          this.workbenchHeaderData &&
          this.workbenchHeaderData.activation_status &&
          this.workbenchHeaderData.activation_status === ACTIVATION_STATUS.LIVE
        )
      },
      isDraft() {
        return (
          this.workbenchHeaderData.activation_status === ACTIVATION_STATUS.DRAFT ||
          this.workbenchHeaderData.activation_status === ACTIVATION_STATUS.NEW
        )
      },
      isWorking() {
        return this.workbenchHeaderData.activation_status === ACTIVATION_STATUS.WORKING
      },
      targetInfoEditable() {
        return !this.viewOnly
      },
      isByodTraitPresent() {
        if (this.workbenchHeaderData.advertiser && this.workbenchHeaderData.advertiser.Advertiser_id) {
          const traits = definitionToListOfTraits(this.$store.getters['workbench/newTargetDefinition'])
          const byodTraitMatchesAdvertiser = traits.some(
            trait => trait.advertiserId && trait.advertiserId === this.workbenchHeaderData.advertiser.Advertiser_id,
          )
          return byodTraitMatchesAdvertiser
        }
        return false
      },
      availableDestinations() {
        return this.$store.getters['workbench/availableDestinations']
      },
      selectedDatasetFamily() {
        return this.$store.getters['workbench/selectedFamilyDataset']
      },
      selectedEpsilonDatasetFamily() {
        return this.selectedDatasetFamily === DATASOURCES.EPSILON
      },
    },
    methods: {
      clickedDropped() {
        // Show a new modal that displays the dropped attributes per quarter
        /**
         * 2. Call the API to get the values
         * 3. Show a loading dots until the api is finished
         * 4. Create the display to show the values in the modal
         */
        this.$store.dispatch(SHOW_DROPPED_ATTRIBUTES_MODEL, true)
      },
      onTargetNameIsValid(valid) {
        if (valid && this.targetName) {
          this.workbenchHeaderData.display_name = this.targetName
          this.$store.dispatch('workbench/' + UPDATE_TARGET_NAME, this.targetName)
        } else {
          this.workbenchHeaderData.display_name = this.originalDisplayName
          this.$store.dispatch('workbench/' + UPDATE_TARGET_NAME, this.workbenchHeaderData.display_name)
        }
      },
      navigateToDashboard() {
        this.$store.dispatch(ROUTE_BACK)
      },
      toggleEditMode() {
        this.editModeActive = !this.editModeActive
        this.savingEnabledWhenToggled = this.$store.state.workbench.enableSaving
        if (this.editModeActive) {
          this.originalDisplayName = this.workbenchHeaderData.display_name
          if (this.workbenchHeaderData.advertiser && this.workbenchHeaderData.advertiser.Advertiser_id) {
            this.originalAdvertiserId = this.workbenchHeaderData.advertiser.Advertiser_id
          }
        }
        if (!this.editModeActive) {
          this.setSaveButtonState()
        }
      },
      itemSelectedHandler(obj) {
        this.currentList = obj.item
      },
      crumbClickHandler(crumb) {
        this.currentList = crumb.node
      },
      clearSearch() {
        this.$refs.searchInput.clearSearch()
      },
      panelClickHandler(e) {
        if (this.isCollapsed) this.toggleCollapse(e)
      },
      toggleCollapse(e) {
        e.stopPropagation()
        this.$emit('collapse-clicked')
      },
      togglePanelSize() {
        this.$emit('change-size-click')
      },
      dropNonMatchingDatasets(datasets) {
        this.attributesTree.dropNodes(datasets)
        this.showOnlyMatchingDatasets(datasets)
        let namesOfDatasetsToDrop = this.attributesTree.droppedNodes.map(x => x.model.name)
        this.searchService.filterDatasets(namesOfDatasetsToDrop)
      },
      showOnlyMatchingDatasets(datasets) {
        let current = this.currentList
        while (current.children.length > 0) {
          current = current.children[0]
        }
        let currentDatasetViewing = current.model.dataset
        let currentRootFamily = current.model.rootFamily
        if (!datasets.includes(currentDatasetViewing) && !datasets.includes(currentRootFamily)) {
          this.currentList = Object.freeze(this.attributesTree.root)
        }
      },
      addBackDroppedDatasets() {
        this.attributesTree.addBackDroppedDatasets()
        this.searchService.resetDatasets()
      },
      createSearchEngine() {
        if (this.setOfAttributes && this.setOfAttributes.length > 0) {
          this.searchService = new SearchService()
          this.attributesTree = new AttributeTree()
          this.setOfAttributes.forEach(attributes => {
            this.searchService.addDataSource(attributes.flat)
            this.attributesTree.addDataSource(attributes.data)
          })
          this.currentList = Object.freeze(this.attributesTree.root)
          if (this.datasetFilter.length > 0) {
            this.dropNonMatchingDatasets(this.datasetFilter)
          } else {
            this.addBackDroppedDatasets()
          }
        }
      },
      getDestinations(destinations = []) {
        if (destinations.length > 0) {
          const orderedArr = Object.values(DESTINATIONS)
          return [...destinations].sort((a, b) => orderedArr.indexOf(a) - orderedArr.indexOf(b)).join(', ')
        } else if (
          this.availableDestinations &&
          this.availableDestinations.length > 0 &&
          this.availableDestinations.includes(DESTINATIONS.SYMPHONY)
        ) {
          return [...this.availableDestinations]
            .sort((a, b) => this.availableDestinations.indexOf(a) - this.availableDestinations.indexOf(b))
            .join(', ')
        } else {
          return ''
        }
      },
      setSaveButtonState() {
        let enableSave = false
        if (this.savingEnabledWhenToggled) {
          enableSave =
            this.workbenchHeaderData.display_name &&
            this.workbenchHeaderData.advertiser.Advertiser_id &&
            this.selectedDatasetFamily
        } else {
          // only when the savingEnabledWhenToggled is set to false, do we not need to worry about the difference in the originalDisplayNames
          let hasDisplayName = this.workbenchHeaderData.display_name !== this.originalDisplayName
          let hasAdvertiser =
            (this.workbenchHeaderData.advertiser.Advertiser_id || '') !== (this.originalAdvertiserId || '')
          enableSave = this.selectedDatasetFamily && (hasDisplayName || hasAdvertiser)
        }
        this.$store.dispatch('workbench/' + SET_ENABLE_SAVE_BUTTON, enableSave)
        if (enableSave) {
          this.$store.dispatch('workbench/' + SET_ENABLE_DOWN_STREAM, !enableSave)
        }
      },
      getCampaignName(xandr_data = {}) {
        if (xandr_data && xandr_data.createdCampaignName) {
          return xandr_data.createdCampaignName + '  |'
        } else if (xandr_data && xandr_data.selectedCampaignId) {
          return xandr_data.campaign ? xandr_data.campaign.campaignName + '  |' : ''
        }
        return ''
      },
      onUpdateAdvertiser(val) {
        this.workbenchHeaderData.advertiser = val
        this.$store.dispatch('workbench/' + SET_ADVERTISER, this.workbenchHeaderData.advertiser)
      },
      setCurrentListToRoot() {
        this.currentList = Object.freeze(this.attributesTree.root)
      },
    },
    beforeMount() {
      this.createSearchEngine()
    },
    watch: {
      canEditTargetHeaderData() {
        if (!this.canEditTargetHeaderData) {
          this.editModeActive = false
          // console.log({cnaEditTargetHeaderData: this.editModeActive})
        }
      },
      setOfAttributes() {
        this.createSearchEngine()
      },
      searchTerm(newValue) {
        if (newValue.length < 1) {
          this.searchResults = []
        } else {
          this.searchResults = this.searchService.search(newValue)
        }
      },
      workbenchHeaderData() {
        if (this.viewOnly) {
          this.$refs.searchInput && this.$refs.searchInput.clearSearch()
        }
        // this.editModeActive = true
      },
      datasetFilter(newVal, oldVal) {
        if (this.datasetFilter.length > 0) {
          let filterChanged = !(
            newVal.length === oldVal.length &&
            newVal.every(function (d) {
              return oldVal.indexOf(d) !== -1
            })
          )
          if (filterChanged) {
            this.addBackDroppedDatasets()
          }
          this.dropNonMatchingDatasets(this.datasetFilter)
        } else {
          this.addBackDroppedDatasets()
        }
      },
    },
  }
</script>

<style lang="scss">
  $container-h-padding: rem(20);
  $bg-color: #435262;
  $header-height: rem(130);
  $header-padding-top: rem(8);
  $header-padding-bottom: rem(14);
  $header-full-height: $header-height + $header-padding-top + $header-padding-bottom;
  $header-nav-font-color: #c6ccd0;
  $header-nav-background-color: #232a32;
  $status-btn-background-color: #676d74;

  .attribute-panel {
    .tooltip {
      position: relative;
      display: inline-block;
    }
    /* Tooltip text */
    .tooltip .tooltiptext {
      visibility: hidden;
      background-color: $header-nav-background-color;
      color: #fff;
      text-align: center;
      padding: 5px 0;
      border-radius: 6px;

      /* Position the tooltip text - see examples below! */
      position: absolute;
      z-index: 1;
      border: white solid 1px;
    }
    /* Show the tooltip text when you mouse over the tooltip container */
    .tooltip:hover .tooltiptext {
      visibility: visible;
      padding: 5px;
    }

    // the margin left is at 50% hopefully this will show the data as needed.
    .tooltip-top {
      bottom: 125%;
      margin-left: -50%;
    }
    /* this will set the ellipsis in the advertiser component
    this make the component much more easier to read and not so clunky
    */
    .selected-tag {
      text-overflow: ellipsis;
      max-width: 200px;
      font-size: 1rem;
      display: block;
      white-space: nowrap;
      overflow: hidden;
    }
    display: flex;
    flex-direction: column;
    background-color: $bg-color;
    color: white;
    overflow: hidden;
    height: 100%;

    &:after {
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      left: rem(-10);
      right: rem(-10);
      height: rem(50);
      box-shadow: 0 rem(-18) rem(30) rgba(#000000, 0.3) inset;
      pointer-events: none;
    }

    &.is-collapsed {
      cursor: pointer;
      transition:
        background-color 0.15s ease-out,
        box-shadow 0.15s ease-out;

      &:hover {
        background-color: lighten($bg-color, 5%);
        box-shadow: rem(-2) 0 0 color(yellow);
      }

      .attribute-panel__body,
      .attribute-panel__header {
        opacity: 0;
        transition-duration: 0s;
        transition-delay: 0s;
      }
    }

    &__body,
    &__header {
      transition: opacity 0.15s $global-easing 0.18s;
    }

    &__header {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      flex: 0 0 auto;
      height: rem(185);
      padding: $header-padding-top $container-h-padding $header-padding-bottom $container-h-padding;
      border-bottom: rem(1) solid color(grays, light);
      box-sizing: border-box;
      &__segment-details {
        display: flex;
        justify-content: space-between;
        font-size: 1.2em;
        margin: 0.5em 0 0 0;
        height: 4em;
        align-items: flex-end;
        &__left {
          align-items: flex-end;
          width: calc(100% - 15px - 10px);
          white-space: nowrap;
          &__name {
            overflow: hidden;
            text-overflow: ellipsis;
            margin-bottom: 30px;
          }
          &__advertiser {
            flex: 1 0 auto;
          }
        }
        &__right {
          display: flex;
          margin-bottom: 35px;
          cursor: pointer;
        }
        .advertiserSelectWrapper {
          background-color: #556678;
          border: 1px solid #2f3943;
          width: 22em;
          color: white !important;
          position: relative;
          bottom: 1px;
          flex: 1;
          margin-left: $container-h-padding;
        }
        &__edit-icon {
          cursor: pointer;
          height: 18px;
          display: flex;
          align-self: baseline;
        }
      }
      &__search-section {
        display: flex;
        align-items: center;
        justify-content: space-between;

        &__logo {
          height: 2em;
          margin-bottom: 6px;
        }

        &__search {
          margin-bottom: rem(25);
          width: 100%;
        }
      }
    }

    &__header-nav {
      display: flex;
      font-size: 0.9em;
      background-color: $header-nav-background-color;
      height: 3.5em;
      align-items: center;
      justify-content: space-between;
      padding: 0 1.5em;
      &__dashboard {
        display: flex;
        align-items: center;
        height: 100%;
        border-right: 2px solid #435261;
        padding-right: 1.5em;
        color: $header-nav-font-color;
        min-height: 4vh;
        cursor: pointer;
        &_icon {
          height: 1.5em;
          padding-right: 1em;
        }
        &_label {
          font-weight: bold;
          padding-left: 5px;
        }
      }
      &__status-details {
        display: flex;

        &__segment-type {
          height: 1em;
          margin-right: 0.75em;
          margin-top: 3px;
        }
        &__status {
          text-transform: uppercase;
          font-weight: bold;
          padding: 0.25em 1.1em;
          background-color: $status-btn-background-color;
          border: 1px solid $header-nav-font-color;
          border-radius: 8px;
          font-size: 0.85em;
          letter-spacing: 1.5px;
        }
      }
    }

    // &__search {
    //   margin-top: rem(20);
    //   margin-bottom: rem(25);
    // }

    &__body {
      position: relative;
      display: flex;
      flex-direction: column;
      height: calc(100% - #{$header-full-height});
      padding-top: $container-h-padding;
      padding-left: $container-h-padding;
    }

    &__back-btn {
      align-self: flex-start;
    }

    &__expand-btn {
      width: 100%;
      padding: 0;
      margin-top: rem(17);
      transform: rotateZ(180deg);
    }
  }

  .cursor-disabled {
    cursor: not-allowed;
    pointer-events: all;
  }
</style>



// WEBPACK FOOTER //
// AttributePanel.vue?64035c54